import { useMemo, useState } from "react";
import { animeList } from "@/mocks/anime";

function shuffle<T>(arr: T[]): T[] {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

export default function BlindBox() {
  const [boxes, setBoxes] = useState(() => shuffle(animeList).slice(0, 4));
  const [opened, setOpened] = useState<Set<number>>(new Set());

  const allOpened = useMemo(
    () => boxes.every((b) => opened.has(b.id as unknown as number)) || boxes.length === 0,
    [boxes, opened]
  );

  const openBox = (id: number) => {
    setOpened((prev) => {
      const next = new Set(prev);
      next.add(id);
      return next;
    });
  };

  const newRound = () => {
    setBoxes(shuffle(animeList).slice(0, 4));
    setOpened(new Set());
  };

  return (
    <section id="blindbox" className="relative py-14 md:py-20 bg-background-100">
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <div className="text-center max-w-2xl mx-auto mb-10 md:mb-14">
          <p className="inline-flex items-center gap-2 rounded-full bg-accent-100 text-accent-700 text-xs md:text-sm font-semibold px-4 py-1.5 mb-4">
            <i className="ri-gift-2-line"></i>
            未知惊喜
          </p>
          <h2 className="font-heading text-3xl md:text-4xl text-foreground-950">盲盒番剧</h2>
          <p className="mt-3 md:mt-4 text-foreground-700 text-sm md:text-base leading-relaxed">
            每一盒都是经过口碑把关的优质番剧，未知的只有「你会抽到哪一部」。点开盒子，见证缘分揭晓。
          </p>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
          {boxes.map((anime, idx) => {
            const isOpen = opened.has(anime.id as unknown as number);
            return (
              <button
                key={`${anime.id}-${idx}`}
                type="button"
                onClick={() => openBox(anime.id as unknown as number)}
                className="perspective-1200 group text-left cursor-pointer"
                aria-label={isOpen ? `已开出 ${anime.title}` : "盲盒未开启，点击揭晓"}
              >
                <div className={`relative preserve-3d transition-transform duration-700 ${isOpen ? "rotate-y-180" : ""}`}>
                  {/* 盒盖 */}
                  <div className="backface-hidden relative aspect-[3/4] rounded-2xl overflow-hidden bg-gradient-to-br from-primary-500 to-primary-700 text-background-50 flex flex-col items-center justify-center gap-2 shadow-sm">
                    <span className="absolute top-3 right-3 w-8 h-8 rounded-full bg-accent-500 text-background-50 flex items-center justify-center text-sm">
                      <i className="ri-question-mark"></i>
                    </span>
                    <i className="ri-gift-fill text-5xl md:text-6xl opacity-90"></i>
                    <p className="font-heading text-lg">盲盒 No.{idx + 1}</p>
                    <p className="text-xs text-background-50/80">点我揭晓缘分</p>
                  </div>
                  {/* 番剧内容 */}
                  <div className="backface-hidden rotate-y-180 absolute inset-0 rounded-2xl overflow-hidden bg-background-50 shadow-sm">
                    <img
                      src={anime.image}
                      alt={`盲盒番剧揭晓：${anime.title}`}
                      className="w-full h-full object-cover object-top"
                    />
                    <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-foreground-950/90 to-transparent px-3 pt-10 pb-3">
                      <p className="font-heading text-white text-base md:text-lg leading-tight">{anime.title}</p>
                      <p className="mt-1 text-white/85 text-xs">
                        <i className="ri-star-fill text-accent-300 mr-0.5"></i>
                        {anime.score}
                      </p>
                    </div>
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        <div className="mt-8 md:mt-10 text-center">
          {allOpened ? (
            <button
              type="button"
              onClick={newRound}
              className="inline-flex items-center gap-2 rounded-full bg-primary-500 hover:bg-primary-600 text-background-50 text-sm md:text-base font-semibold px-8 py-3.5 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-refresh-line"></i>
              再来一盒
            </button>
          ) : (
            <p className="inline-flex items-center gap-2 text-foreground-700 text-sm">
              <i className="ri-hand-heart-line text-primary-500"></i>
              还剩下 {boxes.filter((b) => !opened.has(b.id as unknown as number)).length} 盒没开，勇敢点下去！
            </p>
          )}
        </div>
      </div>
    </section>
  );
}